import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
const viewLabel = (item, index) => {
  if (item.kind === "video") return "Video";
  const labels = {
    front: "Front", side: "Side", back: "Back",
    "demo-side": "Side demo", "demo-back": "Back demo", "demo-detail": "Detail demo",
    zoom: "Detail", additional: "Detail", "360": "360°",
  };
  return labels[item.view] || (index === 0 ? "Front" : `View ${index + 1}`);
};

export default function ProductGallery({ media = [], fallbackImage, alt = "" }) {
  // Build items list — prefer media, else fallback to single image
  const items = media.length > 0
    ? media
    : (fallbackImage ? [{ kind: "image", url: fallbackImage, view: "front" }] : []);

  const [idx, setIdx] = useState(0);

  const mediaKey = media.map((item) => item.url).join("|");

  useEffect(() => {
    setIdx(0);
  }, [mediaKey, fallbackImage]);

  if (items.length === 0) {
    return <div className="h-[360px] bg-white sm:h-[480px] lg:h-[560px]" />;
  }

  const total = items.length;
  const activeIdx = Math.min(idx, total - 1);
  const current = items[activeIdx];
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <div className={`grid gap-3 ${total > 1 ? "lg:grid-cols-[88px_minmax(0,1fr)]" : ""}`} data-testid="product-gallery">
      {/* Main viewer */}
      <div className={`relative h-[360px] overflow-hidden rounded-lg bg-white sm:h-[480px] lg:h-[560px] group ${total > 1 ? "lg:col-start-2 lg:row-start-1" : ""}`}>
        {current.kind === "video" ? (
          <video
            key={current.url}
            src={abs(current.url)}
            controls
            playsInline
            className="w-full h-full object-contain"
            data-testid="gallery-video"
          />
        ) : (
          <img
            key={current.url}
            src={abs(current.url)}
            alt={alt}
            className="w-full h-full object-contain"
            data-testid="gallery-image"
          />
        )}

        {total > 1 && (
          <>
            {/* Prev arrow */}
            <button
              type="button"
              onClick={prev}
              data-testid="gallery-prev"
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-sm"
            >
              <ChevronLeft className="h-5 w-5 text-[#2A1508]" />
            </button>
            {/* Next arrow */}
            <button
              type="button"
              onClick={next}
              data-testid="gallery-next"
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-sm"
            >
              <ChevronRight className="h-5 w-5 text-[#2A1508]" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  data-testid={`gallery-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`transition-all rounded-full ${
                    i === activeIdx
                      ? "w-6 h-2 bg-white"
                      : "w-2 h-2 bg-white/60 hover:bg-white/90"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Real product views only; never manufacture an angle from another item. */}
      {total > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 lg:col-start-1 lg:row-start-1 lg:max-h-[560px] lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1">
          {items.map((m, i) => (
            <button
              key={`${m.url}-${i}`}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Show ${viewLabel(m, i)} view`}
              aria-pressed={i === activeIdx}
              className={`w-[72px] shrink-0 overflow-hidden rounded-lg bg-white text-center transition-all lg:w-[80px] ${
                i === activeIdx ? "ring-2 ring-[#0B7770]" : "ring-1 ring-[#E7E5E4] hover:ring-[#0B7770]/60"
              }`}
            >
              <span className="relative block aspect-square overflow-hidden bg-[#F7F4EF]">
                {m.kind === "video" ? (
                  <>
                    <video src={abs(m.url)} className="h-full w-full object-cover" muted preload="metadata" />
                    <PlayCircle className="absolute inset-0 m-auto h-5 w-5 text-white" />
                  </>
                ) : (
                  <img src={abs(m.url)} alt="" className="h-full w-full object-contain" loading="lazy" />
                )}
              </span>
              <span className="block truncate px-1 py-1 text-[10px] font-medium text-[#315956]">
                {viewLabel(m, i)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
